import { isUploadReady } from "./page-persist";
import type { Page } from "./page";
import type { Script } from "./script";

/** Device-wide view of what the queue still owes the server, and of whether
 * it is making progress. Per-script counts (`script-summary.ts`) answer "did
 * this one go?"; this answers the question nobody could answer before —
 * "is this phone stuck, or just slow?" — without reading a console. */
export interface QueueHealth {
  queued: number;
  uploading: number;
  retrying: number;
  blocked: number;
  /** Failed pages the queue can never pick up: the photo or its checksum
   * never landed in device storage, so `isEligible` skips them and so does
   * the manual trigger. Counted apart from `retrying` because "Retrying" is
   * a promise the queue cannot keep for these — only a retake clears them. */
  needsRetake: number;
  /** Pages still owed to the server: queued + uploading + retrying. Blocked
   * and retake-only pages are excluded — they need a person, not a push. */
  waiting: number;
  /** Highest attempt count among the pages still owed. The cheapest "stuck,
   * not slow" signal there is: backoff doubles from 5s, so a page on its
   * 8th attempt has been failing for something like ten minutes. */
  maxAttempts: number;
  /** Raw `failReason` of the most-attempted page still owed. Deliberately
   * untranslated, unlike the staff-facing copy in `page-status.ts`: this
   * line exists so that whoever is asked "why isn't it uploading?" can read
   * the actual error instead of guessing from a spinner. */
  lastFailReason: string | null;
  /** Soonest automatic retry among pages in backoff, or null if none are. */
  nextAttemptAt: number | null;
  /** `createdAt` of the oldest script that still owes pages — the age of the
   * backlog, which is what makes a stall obvious. */
  oldestWaitingAt: number | null;
}

/** Retake-only: failed, and with no usable image behind it. */
function isRetakeOnly(page: Page): boolean {
  return page.uploadStatus === "failed" && !isUploadReady(page);
}

export function queueHealth(scripts: Script[], pages: Page[]): QueueHealth {
  const owed = pages.filter(
    p =>
      p.uploadStatus === "pending" ||
      p.uploadStatus === "uploading" ||
      (p.uploadStatus === "failed" && isUploadReady(p)),
  );
  const mostAttempts = owed.reduce<Page | null>(
    (worst, p) => (worst === null || p.attemptCount > worst.attemptCount ? p : worst),
    null,
  );
  const backoffs = owed.map(p => p.nextAttemptAt ?? 0).filter(at => at > 0);
  const owedScriptIds = new Set(owed.map(p => p.scriptId));
  const waitingSince = scripts.filter(s => owedScriptIds.has(s.id)).map(s => s.createdAt);

  return {
    queued: owed.filter(p => p.uploadStatus === "pending").length,
    uploading: owed.filter(p => p.uploadStatus === "uploading").length,
    retrying: owed.filter(p => p.uploadStatus === "failed").length,
    blocked: pages.filter(p => p.uploadStatus === "blocked").length,
    needsRetake: pages.filter(isRetakeOnly).length,
    waiting: owed.length,
    maxAttempts: mostAttempts?.attemptCount ?? 0,
    lastFailReason: mostAttempts?.failReason ?? null,
    nextAttemptAt: backoffs.length ? Math.min(...backoffs) : null,
    oldestWaitingAt: waitingSince.length ? Math.min(...waitingSince) : null,
  };
}

/** Coarse on purpose: this is read at arm's length by someone holding a
 * phone, so "2h 14m" is useful and "2 hours, 14 minutes, 9 seconds" is not. */
export function formatAge(ms: number): string {
  const totalMinutes = Math.floor(Math.max(ms, 0) / 60000);
  if (totalMinutes < 1) return "just now";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

/** Whether this device still owes the server anything, or is holding pages
 * that need a person. Nothing below is worth showing when it's false. */
export function hasBacklog(health: QueueHealth): boolean {
  return health.waiting > 0 || health.blocked > 0 || health.needsRetake > 0;
}

export function backlogHeadline(health: QueueHealth): string {
  if (health.waiting === 0) {
    const stuck = health.blocked + health.needsRetake;
    return `${stuck} ${stuck === 1 ? "page needs" : "pages need"} attention`;
  }
  const parts = [`${health.waiting} ${health.waiting === 1 ? "page" : "pages"} waiting`];
  if (health.uploading > 0) parts.push(`${health.uploading} uploading`);
  if (health.retrying > 0) parts.push(`${health.retrying} retrying`);
  return parts.join(" · ");
}

/** The second line: how long this has been going on, and when the queue
 * would next move on its own. Both are what separate "slow" from "stopped"
 * at a glance, and neither was visible anywhere before. */
export function backlogDetail(health: QueueHealth, now: number): string | null {
  const parts: string[] = [];
  if (health.oldestWaitingAt !== null) {
    parts.push(`oldest waiting ${formatAge(now - health.oldestWaitingAt)}`);
  }
  if (health.nextAttemptAt !== null && health.nextAttemptAt > now) {
    parts.push(`next retry in ${Math.max(1, Math.round((health.nextAttemptAt - now) / 1000))}s`);
  }
  if (health.blocked > 0) parts.push(`${health.blocked} blocked`);
  return parts.length ? parts.join(" · ") : null;
}

/** Attempts at which a backlog stops being "the network is slow today".
 * Attempt 6 is roughly two and a half minutes of doubling backoff — long
 * enough that a scanner standing there deserves to be told, short enough
 * that it fires during the exam session rather than after it. */
const STUCK_ATTEMPTS = 6;

/** The one line that turns "I don't know if they're stuck" into an answer,
 * or null when the queue is simply working through a backlog. Returns the
 * most actionable problem first: a page needing a retake is work for the
 * person holding the phone, while a repeated upload failure is work for
 * whoever runs the server. */
export function stuckWarning(health: QueueHealth): string | null {
  if (health.needsRetake > 0) {
    const n = health.needsRetake;
    return `${n} ${n === 1 ? "page" : "pages"} can't be uploaded from this device — open the script and retake ${n === 1 ? "it" : "them"}.`;
  }
  if (health.maxAttempts >= STUCK_ATTEMPTS) {
    const reason = health.lastFailReason ?? "no response from the server";
    return `Uploads keep failing after ${health.maxAttempts} attempts — ${reason}. Show this to whoever runs the server.`;
  }
  return null;
}
